frappe.ready(function() {
	if (!window.location.pathname || !window.location.pathname.startsWith("/cart")) {
		return;
	}

	function getCartApi() {
		return (
			window.shopping_cart ||
			(window.webshop && window.webshop.webshop && window.webshop.webshop.shopping_cart)
		);
	}

	var isSubmitting = false;
	var activeSubmission = null;
	var REQUEST_TIMEOUT_MS = 30000;
	var WATCHDOG_TIMEOUT_MS = 35000;

	// ── Offline queue for checkout submission ──────────────────────────────
	// Gated on Website/Webshop Settings.checkout_local_queue_enabled (default
	// off), same rollout precedent as cart_resilience.js's own
	// cart_local_queue_enabled. Deliberately its own small, focused
	// implementation rather than sharing cart_resilience.js's queue machinery
	// directly: checkout only ever has ONE thing in flight at a time (the
	// current cart's order), not a growing list of independent operations, so
	// cart's op-array/prune/version machinery would be solving a problem
	// checkout doesn't have. Modeled on the same proven conventions (host+user
	// scoped storage key, TTL, connectivity-driven retry) without the
	// multi-op complexity.
	//
	// place_order is already idempotent server-side
	// (simple_checkout.py's _find_recent_duplicate_checkout_order /
	// _replay_checkout_redirect_payload) -- that's what makes it safe to
	// blindly replay the exact same request on reconnect instead of needing
	// new correctness work here.
	var CHECKOUT_QUEUE_TTL_MS = 30 * 60 * 1000; // 30 min -- an order intent older than this is stale, don't surprise-submit it
	var checkoutQueueEnabled = false;
	var checkoutQueueFlagLoaded = false;
	var pendingRetryUnsubscribe = null;

	function currentUser() {
		return (frappe && frappe.session && frappe.session.user) || "Guest";
	}

	function checkoutQueueStorageKey() {
		return ["webhq_pending_checkout", window.location.host, currentUser()].join(":");
	}

	function storageAvailable() {
		try {
			if (!window.localStorage) return false;
			var probe = "__checkout_queue_probe__";
			window.localStorage.setItem(probe, "1");
			window.localStorage.removeItem(probe);
			return true;
		} catch (e) {
			return false;
		}
	}

	function loadPendingCheckout() {
		if (!storageAvailable()) return null;
		try {
			var raw = window.localStorage.getItem(checkoutQueueStorageKey());
			if (!raw) return null;
			var parsed = JSON.parse(raw);
			if (!parsed || Date.now() - (parsed.queued_at || 0) > CHECKOUT_QUEUE_TTL_MS) {
				window.localStorage.removeItem(checkoutQueueStorageKey());
				return null;
			}
			return parsed;
		} catch (e) {
			return null;
		}
	}

	function savePendingCheckout(record) {
		if (!storageAvailable()) return;
		try {
			window.localStorage.setItem(checkoutQueueStorageKey(), JSON.stringify(record));
		} catch (e) {
			// Storage quota errors leave this best-effort -- worst case, a
			// dropped connection just falls back to the pre-existing manual
			// "check your orders" behavior.
		}
	}

	function clearPendingCheckout() {
		if (!storageAvailable()) return;
		try {
			window.localStorage.removeItem(checkoutQueueStorageKey());
		} catch (e) {
			// Ignore.
		}
		clearIndexedDbPending();
	}

	// ── IndexedDB mirror: the part that survives a closed tab ──────────────
	// localStorage above covers "queue it, retry once RaftorConnectivity says
	// we're back online, as long as this tab is still open" -- that's most of
	// the value, but a service worker (which is what has to act *after* the
	// tab is gone -- that's the whole point of the Background Sync API) can't
	// read localStorage at all; it's page-scoped, not origin-scoped the way
	// IndexedDB is. This is a small, deliberately parallel write into
	// IndexedDB (not a replacement for the localStorage path -- both are
	// written, localStorage drives the fast in-tab retry, IndexedDB is what
	// raftor_pwahq's service worker (www/pwa_sw/sw.js, a different app --
	// this DB is the cross-app contract between the two) reads on its own
	// "sync" event handler).
	//
	// Schema is intentionally tiny and must stay in lockstep with sw.js's own
	// copy of these same constants: DB "raftor-offline-queue", version 1,
	// one object store "checkout" (keyPath "key"), one record per
	// host:user, shape {key, payment_mode, queued_at, csrf_token}.
	var OFFLINE_DB_NAME = "raftor-offline-queue";
	var OFFLINE_DB_VERSION = 1;
	var OFFLINE_STORE_NAME = "checkout";
	var SYNC_TAG = "raftor-checkout-queue";

	function openOfflineDb() {
		return new Promise(function (resolve, reject) {
			if (!window.indexedDB) {
				reject(new Error("indexedDB unavailable"));
				return;
			}
			var req = window.indexedDB.open(OFFLINE_DB_NAME, OFFLINE_DB_VERSION);
			req.onupgradeneeded = function () {
				var db = req.result;
				if (!db.objectStoreNames.contains(OFFLINE_STORE_NAME)) {
					db.createObjectStore(OFFLINE_STORE_NAME, { keyPath: "key" });
				}
			};
			req.onsuccess = function () {
				resolve(req.result);
			};
			req.onerror = function () {
				reject(req.error);
			};
		});
	}

	function writeIndexedDbPending(record) {
		return openOfflineDb()
			.then(function (db) {
				return new Promise(function (resolve, reject) {
					var tx = db.transaction(OFFLINE_STORE_NAME, "readwrite");
					tx.objectStore(OFFLINE_STORE_NAME).put(
						Object.assign({ key: checkoutQueueStorageKey() }, record)
					);
					tx.oncomplete = function () {
						db.close();
						resolve();
					};
					tx.onerror = function () {
						db.close();
						reject(tx.error);
					};
				});
			})
			.catch(function () {
				// IndexedDB unsupported/blocked (private browsing in some
				// browsers, quota) -- the localStorage path above already
				// covers the same-tab retry case either way, so this is a
				// clean degrade, not a failure.
			});
	}

	function clearIndexedDbPending() {
		openOfflineDb()
			.then(function (db) {
				var tx = db.transaction(OFFLINE_STORE_NAME, "readwrite");
				tx.objectStore(OFFLINE_STORE_NAME).delete(checkoutQueueStorageKey());
				tx.oncomplete = function () {
					db.close();
				};
			})
			.catch(function () {
				// Nothing to clear if it was never opened.
			});
	}

	/** Ask the service worker to replay this the moment the OS/browser
	 * detects real connectivity, including after this tab is gone --
	 * Background Sync isn't Safari-supported, hence the try/catch; the
	 * localStorage+RaftorConnectivity path above is what covers that browser
	 * (and any other engine without Background Sync) as long as the tab
	 * stays open, which is the common case anyway. */
	function registerBackgroundSync() {
		if (!("serviceWorker" in navigator)) return;
		navigator.serviceWorker.ready
			.then(function (registration) {
				if (registration && registration.sync && typeof registration.sync.register === "function") {
					return registration.sync.register(SYNC_TAG);
				}
			})
			.catch(function () {
				// No active SW registration, or Background Sync unsupported --
				// the in-tab retry path is unaffected either way.
			});
	}

	function ensureCheckoutQueueFlag(cb) {
		if (checkoutQueueFlagLoaded) {
			cb();
			return;
		}
		var flags = window.checkoutFlags || {};
		if (typeof flags.checkout_local_queue_enabled !== "undefined") {
			checkoutQueueEnabled = !!flags.checkout_local_queue_enabled;
			checkoutQueueFlagLoaded = true;
			cb();
			return;
		}
		// window.checkoutFlags (populated by webshop_simple_checkout_settings.js
		// from the same get_simple_checkout_flags call cart_resilience.js
		// already makes) may not have loaded yet on a very first paint --
		// fall back to asking directly rather than assuming disabled.
		frappe.call({
			type: "GET",
			method: "raftor_webhq.checkout.simple_checkout.get_simple_checkout_flags",
			callback: function (r) {
				var f = (r && r.message) || {};
				checkoutQueueEnabled = !!f.checkout_local_queue_enabled;
				checkoutQueueFlagLoaded = true;
				cb();
			},
			error: function () {
				checkoutQueueEnabled = false;
				checkoutQueueFlagLoaded = true;
				cb();
			},
		});
	}

	function finalizeSubmission(button) {
		if (!activeSubmission || activeSubmission.finalized) {
			return;
		}
		activeSubmission.finalized = true;

		if (activeSubmission.watchdogId) {
			window.clearTimeout(activeSubmission.watchdogId);
		}

		var cartApi = getCartApi();
		if (cartApi && typeof cartApi.unfreeze === "function") {
			cartApi.unfreeze();
		} else {
			var freezeEl = document.getElementById("freeze");
			if (freezeEl) {
				freezeEl.remove();
			}
		}
		isSubmitting = false;
		if (button) {
			button.disabled = false;
		}
		activeSubmission = null;
	}

	function showCartError(response, button) {
		finalizeSubmission(button);

		var msg = "";
		if (response && response._server_messages) {
			msg = JSON.parse(response._server_messages || []).join("<br>");
		}

		var errorNode = document.getElementById("cart-error");
		if (window.jQuery && typeof window.jQuery === "function") {
			window.jQuery("#cart-error")
				.empty()
				.html(msg || frappe._("Something went wrong!"))
				.toggle(true);
			return;
		}
		if (errorNode) {
			errorNode.innerHTML = msg || frappe._("Something went wrong!");
			errorNode.style.display = "block";
		}
	}

	function resolvePaymentMode() {
		var paymentMode = "PREPAID";
		var checkoutFlags = window.checkoutFlags || {};
		if (checkoutFlags.selected_payment_mode) {
			paymentMode = checkoutFlags.selected_payment_mode;
		}
		if (checkoutFlags.hide_payment_on_webshop) {
			paymentMode = null;
		}
		var selector = document.getElementById("ce-payment-mode-input");
		if (!checkoutFlags.hide_payment_on_webshop && selector && selector.value) {
			paymentMode = selector.value;
		}
		return paymentMode;
	}

	/** Shared success/redirect handling for both a live submission and a
	 * queued retry -- same payload shape either way. */
	function handleOrderResponse(r, button) {
		if (r.exc) {
			showCartError(r, button);
			return;
		}

		var payload = r.message || {};
		var redirectTo = "";

		if (typeof payload === "string") {
			redirectTo = "/order-success?order_id=" + encodeURIComponent(payload);
		} else if (payload.redirect_to) {
			redirectTo = payload.redirect_to;
		} else if (payload.order_id) {
			redirectTo = payload.payment_required
				? (payload.order_redirect || ("/orders/" + encodeURIComponent(payload.order_id)))
				: "/order-success?order_id=" + encodeURIComponent(payload.order_id);
		}

		if (!redirectTo) {
			showCartError(null, button);
			return;
		}

		clearPendingCheckout();
		if (window.jQuery && typeof window.jQuery === "function") {
			window.jQuery(button).hide();
		} else if (button) {
			button.style.display = "none";
		}
		window.location.replace(redirectTo);
	}

	function showQueuedMessage() {
		if (frappe && typeof frappe.show_alert === "function") {
			frappe.show_alert(
				{
					message: frappe._("You're offline. Your order will be submitted automatically once you're back online."),
					indicator: "orange",
				},
				6
			);
		}
	}

	/** Replays the exact same place_order call a queued/interrupted
	 * submission would have made. Safe to call blindly on reconnect --
	 * place_order is idempotent server-side (_find_recent_duplicate_checkout_order
	 * / _replay_checkout_redirect_payload), so a request that actually
	 * succeeded before the response was lost just replays the same redirect
	 * instead of creating a second order. */
	function attemptRetry(record, button) {
		frappe.call({
			type: "POST",
			method: "webshop.webshop.shopping_cart.cart.place_order",
			timeout: REQUEST_TIMEOUT_MS,
			args: {
				payment_mode: record.payment_mode,
			},
			callback: function (r) {
				handleOrderResponse(r, button);
			},
			error: function () {
				// Still failing -- leave the record in place (it wasn't
				// cleared) and wait for the next genuine online event rather
				// than busy-retrying into a connection that's still down.
				subscribeRetryOnReconnect();
			},
		});
	}

	function subscribeRetryOnReconnect() {
		if (pendingRetryUnsubscribe || !window.RaftorConnectivity) return;
		pendingRetryUnsubscribe = window.RaftorConnectivity.on(function (state) {
			if (!state.online) return;
			var record = loadPendingCheckout();
			if (!record) {
				if (pendingRetryUnsubscribe) {
					pendingRetryUnsubscribe();
					pendingRetryUnsubscribe = null;
				}
				return;
			}
			if (pendingRetryUnsubscribe) {
				pendingRetryUnsubscribe();
				pendingRetryUnsubscribe = null;
			}
			attemptRetry(record, document.getElementById("place-order"));
		});
	}

	function queuePendingCheckout(paymentMode, button) {
		var record = {
			payment_mode: paymentMode,
			queued_at: Date.now(),
			// Currently always "None" on this site's own website pages (only
			// the desk app shell sets a real token -- confirmed live), but
			// captured here regardless of today's value so this keeps working
			// unchanged if CSRF protection is ever turned on for storefront
			// requests -- sw.js sends it back exactly as stored, verbatim.
			csrf_token: (window.frappe && window.frappe.csrf_token) || null,
		};
		savePendingCheckout(record);
		writeIndexedDbPending(record).then(registerBackgroundSync);
		showQueuedMessage();
		if (window.jQuery && typeof window.jQuery === "function") {
			window.jQuery(button).hide();
		} else if (button) {
			button.style.display = "none";
		}
		subscribeRetryOnReconnect();
	}

	/** A failure this function is called for is specifically a connectivity
	 * failure (frappe.call's error callback, or the watchdog firing with
	 * neither callback nor error ever resolving) -- a server-side rejection
	 * (validation error, etc.) already surfaced through handleOrderResponse's
	 * r.exc branch above and never reaches here. Only queue when the queue
	 * is enabled AND the browser genuinely reports being offline -- a fast
	 * one-off network blip that isn't a real connectivity drop should still
	 * surface the existing manual-retry message rather than silently queue
	 * and wait for an "online" event that (since the browser thinks it's
	 * already online) may never come. */
	function maybeQueueOrShowError(paymentMode, button) {
		var connectivityDown = window.RaftorConnectivity && !window.RaftorConnectivity.state().online;
		if (checkoutQueueEnabled && connectivityDown) {
			finalizeSubmission(button);
			queuePendingCheckout(paymentMode, button);
			return;
		}
		showCartError(null, button);
	}

	function placeOrderAndRedirect(button) {
		var cartApi = getCartApi();
		if (!cartApi || typeof cartApi.freeze !== "function" || typeof cartApi.unfreeze !== "function") {
			showCartError(null, button);
			return;
		}

		cartApi.freeze();
		activeSubmission = {
			finalized: false,
			watchdogId: null,
		};
		var paymentMode = resolvePaymentMode();
		activeSubmission.watchdogId = window.setTimeout(function() {
			if (!activeSubmission || activeSubmission.finalized) {
				return;
			}
			maybeQueueOrShowError(paymentMode, button);
			if (!checkoutQueueEnabled || (window.RaftorConnectivity && window.RaftorConnectivity.state().online)) {
				frappe.msgprint(frappe._("Order request timed out. Please check your orders before retrying."));
			}
		}, WATCHDOG_TIMEOUT_MS);

		return frappe.call({
			type: "POST",
			method: "webshop.webshop.shopping_cart.cart.place_order",
			btn: button,
			timeout: REQUEST_TIMEOUT_MS,
			args: {
				payment_mode: paymentMode,
			},
			callback: function(r) {
				finalizeSubmission(button);
				handleOrderResponse(r, button);
			},
			error: function() {
				finalizeSubmission(button);
				maybeQueueOrShowError(paymentMode, button);
			},
		});
	}

	function installPlaceOrderWrapper() {
		var cartApi = getCartApi();
		if (!cartApi || typeof cartApi.place_order !== "function") {
			return false;
		}
		if (typeof cartApi.freeze !== "function" || typeof cartApi.unfreeze !== "function") {
			return false;
		}
		if (cartApi.place_order && cartApi.place_order.__webhq_wrapped) {
			return true;
		}

		var originalPlaceOrder = cartApi.place_order;
		var wrapped = function(btn) {
			if (isSubmitting) {
				return;
			}
			isSubmitting = true;
			if (btn) {
				btn.disabled = true;
			}
			return placeOrderAndRedirect(btn);
		};
		wrapped.__webhq_wrapped = true;
		wrapped.__webhq_original = originalPlaceOrder;
		cartApi.place_order = wrapped;
		return true;
	}

	var attempts = 0;
	var maxAttempts = 25;
	var installTimer = window.setInterval(function() {
		attempts += 1;
		if (installPlaceOrderWrapper() || attempts >= maxAttempts) {
			window.clearInterval(installTimer);
		}
	}, 100);

	// Load the feature flag proactively (rather than only lazily on first
	// submit attempt) so it's already resolved by the time a Dealer actually
	// clicks "Place Order" on a slow connection -- and resume a checkout that
	// was queued in an earlier tab/session (e.g. the tab was closed while
	// offline) if one is still on file and not yet stale.
	ensureCheckoutQueueFlag(function () {
		if (!checkoutQueueEnabled) return;
		var record = loadPendingCheckout();
		if (!record) return;
		if (window.RaftorConnectivity && window.RaftorConnectivity.state().online) {
			attemptRetry(record, document.getElementById("place-order"));
		} else {
			showQueuedMessage();
			subscribeRetryOnReconnect();
		}
	});
});
